import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { completenessPercent, identityIsReady, missingRequiredFields } from "../lib/brands/completeness.js";
import { isAdmin, canEdit } from "../constants.js";
import IdentityEditor from "./IdentityEditor";
import ReferencesTab from "./ReferencesTab";
import OffersTab from "./OffersTab";
import AssetsTab from "./AssetsTab";
import "./BrandDetail.css";

const TABS = [
  { key: "identity", label: "Identity" },
  { key: "references", label: "References" },
  { key: "offers", label: "Offers" },
  { key: "assets", label: "Assets" },
];

export default function BrandDetail({
  config,
  onSaveBrand,
  isSavingBrand,
  onSaveReferences,
  isSavingReferences,
  onSaveOffers,
  isSavingOffers,
  onDeleteBrand,
}) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const entryIndex = (config.config ?? []).findIndex((item) => item.brand.slug === slug);
  const entry = entryIndex !== -1 ? config.config[entryIndex] : null;
  const [localIdentity, setLocalIdentity] = useState(entry?.identity ?? null);
  const [saveError, setSaveError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset the draft whenever we land on a different brand.
  useEffect(() => {
    setLocalIdentity(entry?.identity ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const tabParam = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.key === tabParam) ? tabParam : "identity";

  function selectTab(key) {
    setSearchParams({ tab: key }, { replace: true });
  }

  if (!entry) {
    return (
      <div className="brand-detail__not-found">
        <p>Brand not found.</p>
        <Link to="/brands" className="brand-detail__back-link">
          Back to Brands
        </Link>
      </div>
    );
  }

  const { brand, references, offers } = entry;
  const isActive = brand.status === "active";
  // Same fix as Brands.js — logoPath is a legacy field, always null now
  // that logos are real references. Default to the first logo-kind one.
  const logoUrl = (references ?? []).find((r) => r.kind === "logo")?.url ?? null;

  const livePct = completenessPercent(localIdentity);
  const hasUnsavedChanges = JSON.stringify(localIdentity) !== JSON.stringify(entry.identity);
  const missing = missingRequiredFields(localIdentity);

  const canSave = identityIsReady(localIdentity) && hasUnsavedChanges && isAdmin(config.userRole);
  const canGenerate = isActive && !hasUnsavedChanges && canEdit(config.userRole);

  // The API call itself lives entirely in App.js — this just requests it.
  // Catches failures now that updateBrandField can genuinely throw (e.g.
  // a lock timeout, or the brand no longer existing server-side) -
  // previously there was no way to distinguish a failed save from a
  // successful one at all.
  async function handleSaveClick() {
    setSaveError(null);
    try {
      await onSaveBrand(brand.id, localIdentity);
    } catch (err) {
      setSaveError("Couldn't save changes. Please try again.");
    }
  }

  function handleGenerateClick() {
    navigate(`/generate?brand=${brand.slug}`);
  }

  // Two-step: first click arms the confirmation, second click actually
  // deletes - a single click on a destructive, irreversible action is
  // too easy to trigger by accident.
  async function handleDeleteClick() {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await onDeleteBrand(brand.id);
      navigate("/brands");
    } catch (err) {
      setDeleteError("Couldn't delete this brand. Please try again.");
      setConfirmingDelete(false);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="brand-detail">
      {/* Header */}
      <div className="brand-detail__header">
        {logoUrl ? (
          <img src={logoUrl} alt={`${brand.name} logo`} className="brand-detail__logo" />
        ) : (
          <span className="brand-detail__logo-fallback">
            {brand.name?.charAt(0).toUpperCase() ?? "?"}
          </span>
        )}

        <div className="brand-detail__info">
          <h1 className="brand-detail__name">{brand.name}</h1>
          {brand.websiteUrl ? (
            <a
              href={brand.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="brand-detail__website"
            >
              {brand.websiteUrl}
            </a>
          ) : null}
        </div>

        <div className="brand-detail__meta">
          <div className="brand-detail__completeness">
            <p className="brand-detail__completeness-pct">
              {livePct}% complete
              {hasUnsavedChanges ? <span className="brand-detail__unsaved-dot" /> : null}
            </p>
            <div className="brand-detail__completeness-track">
              <div
                className={`brand-detail__completeness-fill${
                  livePct === 100 ? " brand-detail__completeness-fill--done" : ""
                }`}
                style={{ width: `${Math.max(livePct, 4)}%` }}
              />
            </div>
          </div>

          {saveError ? <p className="brand-detail__save-error">{saveError}</p> : null}

          <button
            type="button"
            onClick={handleSaveClick}
            disabled={!canSave || isSavingBrand}
            title={
              !isAdmin(config.userRole)
                ? "Only admins can edit brand identity"
                : missing.length > 0
                  ? `Complete required fields: ${missing.join(", ")}`
                  : !hasUnsavedChanges
                    ? "No changes to save"
                    : undefined
            }
            className="brand-detail__save-btn"
          >
            {isSavingBrand ? "Saving…" : isActive ? "Save changes" : "Save identity"}
          </button>

          {isActive ? (
            <button
              type="button"
              onClick={handleGenerateClick}
              disabled={!canGenerate}
              title={
                !canEdit(config.userRole)
                  ? "Editors and admins only"
                  : hasUnsavedChanges
                    ? "Save your changes before generating"
                    : undefined
              }
              className="btn btn--primary"
            >
              Generate
            </button>
          ) : null}

          {deleteError ? <p className="brand-detail__save-error">{deleteError}</p> : null}

          {isAdmin(config.userRole) ? (
            <div className="brand-detail__delete-row">
              {confirmingDelete ? (
                <>
                  <span className="brand-detail__delete-confirm-text">Delete “{brand.name}”? This can't be undone.</span>
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className="brand-detail__delete-btn brand-detail__delete-btn--confirm"
                  >
                    {isDeleting ? "Deleting…" : "Yes, delete"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isDeleting}
                    className="brand-detail__delete-cancel"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" onClick={handleDeleteClick} className="brand-detail__delete-btn">
                  Delete brand
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Tab nav */}
      <nav className="brand-detail__tabs" aria-label="Brand sections">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => selectTab(t.key)}
            aria-current={activeTab === t.key ? "page" : undefined}
            className={`brand-detail__tab${
              activeTab === t.key ? " brand-detail__tab--active" : ""
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="brand-detail__panel">
        {activeTab === "identity" ? (
          <IdentityEditor identity={localIdentity} onChange={setLocalIdentity} role={config.userRole} />
        ) : null}

        {activeTab === "references" ? (
          isActive ? (
            <ReferencesTab
              config={config}
              brand={brand}
              references={references}
              onSaveReferences={onSaveReferences}
            />
          ) : (
            <p className="brand-detail__placeholder">
              Save your brand identity first to unlock references.
            </p>
          )
        ) : null}

        {activeTab === "offers" ? (
          isActive ? (
            <OffersTab brand={brand} offers={offers} onSaveOffers={onSaveOffers} role={config.userRole} />
          ) : (
            <p className="brand-detail__placeholder">
              Save your brand identity first to unlock offers.
            </p>
          )
        ) : null}

        {activeTab === "assets" ? (
          isActive ? (
            <AssetsTab appConfig={config} brand={brand} />
          ) : (
            <p className="brand-detail__placeholder">
              Save your brand identity first to unlock assets.
            </p>
          )
        ) : null}
      </div>
    </div>
  );
}